'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearFilters, fetchApuracoes, selectApuracoes, selectApuracoesError, selectApuracoesFilters, selectApuracoesLoading, selectApuracoesSummary, setFilters } from '../store';
import type { Apuracao, ApuracaoListFilters, ApuracaoStatus, TipoCliente } from '../types';
interface UseApuracoesListResult {
  apuracoes: Apuracao[];
  filteredApuracoes: Apuracao[];
  summary: ReturnType<typeof selectApuracoesSummary>;
  filters: ApuracaoListFilters;
  loading: boolean;
  error: string | null;
  isFiltering: boolean;
  refresh: () => void;
  updateFilters: (patch: Partial<ApuracaoListFilters>) => void;
  resetFilters: () => void;
}
const aplicarFiltros = (apuracoes: Apuracao[], filters: ApuracaoListFilters): Apuracao[] => {
  const buscaNormalizada = filters.busca.trim().toLowerCase();
  return apuracoes.filter(apuracao => {
    if (filters.status.length > 0 && !filters.status.includes(apuracao.status)) {
      return false;
    }
    if (filters.tipoCliente.length > 0 && !filters.tipoCliente.includes(apuracao.tipoCliente)) {
      return false;
    }
    if (buscaNormalizada && !apuracao.tenantNome.toLowerCase().includes(buscaNormalizada) && !apuracao.produtoNome.toLowerCase().includes(buscaNormalizada)) {
      return false;
    }
    if (filters.periodoInicio && apuracao.periodoFim.slice(0, 10) < filters.periodoInicio) {
      return false;
    }
    if (filters.periodoFim && apuracao.periodoInicio.slice(0, 10) > filters.periodoFim) {
      return false;
    }
    if (filters.somenteComErros && apuracao.eventosRejeitadosCount === 0) {
      return false;
    }
    return true;
  });
};
const VALID_STATUSES: ApuracaoStatus[] = ['ABERTO', 'FECHADO', 'REABERTO'];
const VALID_TENANT_TYPES: TipoCliente[] = ['AVULSO', 'CONVENIADO'];
function parseFiltersFromUrl(params: URLSearchParams): Partial<ApuracaoListFilters> {
  const out: Partial<ApuracaoListFilters> = {};
  const busca = params.get('q');
  if (busca) out.busca = busca;
  const status = params.get('status');
  if (status) {
    out.status = status.split(',').filter((s): s is ApuracaoStatus => VALID_STATUSES.includes(s as ApuracaoStatus));
  }
  const tipoCliente = params.get('tipoCliente');
  if (tipoCliente) {
    out.tipoCliente = tipoCliente.split(',').filter((s): s is TipoCliente => VALID_TENANT_TYPES.includes(s as TipoCliente));
  }
  const inicio = params.get('inicio');
  if (inicio) out.periodoInicio = inicio;
  const fim = params.get('fim');
  if (fim) out.periodoFim = fim;
  if (params.get('erros') === '1') out.somenteComErros = true;
  return out;
}
function buildSearchString(filters: ApuracaoListFilters): string {
  const params = new URLSearchParams();
  if (filters.busca.trim()) params.set('q', filters.busca.trim());
  if (filters.status.length > 0) params.set('status', filters.status.join(','));
  if (filters.tipoCliente.length > 0) params.set('tipoCliente', filters.tipoCliente.join(','));
  if (filters.periodoInicio) params.set('inicio', filters.periodoInicio);
  if (filters.periodoFim) params.set('fim', filters.periodoFim);
  if (filters.somenteComErros) params.set('erros', '1');
  return params.toString();
}
export function useApuracoesList(): UseApuracoesListResult {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const apuracoes = useAppSelector(selectApuracoes);
  const loading = useAppSelector(selectApuracoesLoading);
  const error = useAppSelector(selectApuracoesError);
  const summary = useAppSelector(selectApuracoesSummary);
  const filters = useAppSelector(selectApuracoesFilters);
  useEffect(() => {
    const fromUrl = parseFiltersFromUrl(new URLSearchParams(searchParams.toString()));
    if (Object.keys(fromUrl).length > 0) {
      dispatch(setFilters(fromUrl));
    }
    dispatch(fetchApuracoes());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot bootstrap on mount
  }, [dispatch]);
  useEffect(() => {
    const next = buildSearchString(filters);
    const current = searchParams.toString();
    if (next === current) return;
    router.replace(`${pathname}${next ? `?${next}` : ''}`, {
      scroll: false
    });
  }, [filters, pathname, router, searchParams]);
  const filteredApuracoes = useMemo(() => aplicarFiltros(apuracoes, filters), [apuracoes, filters]);
  const isFiltering = useMemo(() => filters.status.length > 0 || filters.tipoCliente.length > 0 || filters.busca.trim().length > 0 || filters.periodoInicio !== null || filters.periodoFim !== null || filters.somenteComErros, [filters]);
  const refresh = useCallback(() => {
    dispatch(fetchApuracoes());
  }, [dispatch]);
  const updateFilters = useCallback((patch: Partial<ApuracaoListFilters>) => {
    dispatch(setFilters(patch));
  }, [dispatch]);
  const resetFilters = useCallback(() => {
    dispatch(clearFilters());
  }, [dispatch]);
  return {
    apuracoes,
    filteredApuracoes,
    summary,
    filters,
    loading,
    error,
    isFiltering,
    refresh,
    updateFilters,
    resetFilters
  };
}